import { createApi } from '@reduxjs/toolkit/query/react';
import { Application } from '@typeRoots/common/application';
import {
  setApplication,
  updateApplication,
  updateApplicationCreated,
} from '../slice/applicationSlice';
import { initialApplicationState } from '../slice/initialState';
import { Quote } from 'src/features/quotes/slice/initialState';
import { setQuotes } from 'src/features/quotes/slice/quoteSlice';
import { baseQueryWithProgress } from 'src/helpers/fetchQueryHelper';

export const applicationApi = createApi({
  // Unique key for the API slice in Redux state
  reducerPath: 'applicationApi',

  // Configure base query settings, including the base URL for all requests
  baseQuery: baseQueryWithProgress,

  // Define cache tag types for automatic cache invalidation
  tagTypes: ['Application'],

  // Define API endpoints (queries and mutations)
  endpoints: (builder) => ({
    getApplicationByCompanyId: builder.query<Application, number>({
      query: (id) => `/application/company/${id}`,
      async onQueryStarted(_params, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // set application object in redux store
          dispatch(setApplication(data));
          dispatch(updateApplicationCreated(true));
        } catch (error) {
          console.log('Error fetching application data', error);
          dispatch(setApplication(initialApplicationState));
          dispatch(updateApplicationCreated(false));
        }
      },
      // Tagging Application to automatically refresh this cache when needed
      providesTags: ['Application'],
    }),
    sendApplicationIdToGenerateQuote: builder.query<Quote, number>({
      query: (applicationId) => `coverwhale/quote/generate/${applicationId}`,
      async onQueryStarted(_params, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // set quotes object in redux store
          dispatch(data ? setQuotes([data] as Quote[]) : setQuotes([] as Quote[]));
        } catch (error) {
          console.log('Error fetching coverwhale quote data', error);
        }
      },
      // Tagging Application to automatically refresh this cache when needed
      providesTags: ['Application'],
    }),
    //add lint ignore here
    // eslint-disable-next-line
    getIndication: builder.query<any, number>({
      query: (applicationId) => `tenfour/generate/indication/${applicationId}`,
      async onQueryStarted(_params, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Handle the response data as needed, e.g., dispatch actions
          console.log('Indication data:', data);
        } catch (error) {
          console.log('Error fetching indication data', error);
        }
      },
      providesTags: ['Application'],
    }),

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendApplicationIdToGenerateAMICQuote: builder.query<any, number>({
      query: (applicationId) => `quotes/amic/generate/${applicationId}`,
      async onQueryStarted(_params, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // AMIC is async (fire-and-forget) - returns { message, jobId }
          // Quote bot processes in background, no instant quote returned
          console.log('AMIC automation triggered:', data);
        } catch (error) {
          console.log('Error triggering AMIC automation', error);
        }
      },
      providesTags: ['Application'],
    }),

    // Create application endpoint
    createApplication: builder.mutation<Application, Partial<Application>>({
      query: (newApplication) => ({
        url: '/application',
        method: 'POST',
        body: newApplication,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // set created application object in redux store

          dispatch(setApplication(data));
          dispatch(updateApplicationCreated(true));
        } catch (e) {
          console.log('Create Application error: ', e);
        }
      },
    }),

    // Update application endpoint
    updateApplication: builder.mutation<
      Application,
      { id: number; updatedData: Partial<Application> }
    >({
      query: ({ id, updatedData }) => ({
        url: `/application/${id}`,
        method: 'PATCH',
        body: JSON.stringify(updatedData),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          console.log(params.updatedData, 'data');
          dispatch(updateApplication({ ...params.updatedData, id: params.id }));
          dispatch(updateApplicationCreated(true));
        } catch (e) {
          console.log('Update Application data error', e);
        }
      },
    }),
  }),
});

// Export generated hooks for each endpoint to use them in components
export const {
  useGetApplicationByCompanyIdQuery,
  useLazyGetApplicationByCompanyIdQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useSendApplicationIdToGenerateQuoteQuery,
  useLazySendApplicationIdToGenerateQuoteQuery,
  useGetIndicationQuery,
  useLazyGetIndicationQuery,
  useSendApplicationIdToGenerateAMICQuoteQuery,
  useLazySendApplicationIdToGenerateAMICQuoteQuery,
} = applicationApi;
